import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  ActiveDriver,
  DashboardData,
  DriverShiftOverviewResponse,
  ListAvailableDriversResponseDriver,
  LoHiOrderStatsRegion,
} from '../../../types';
import { AuthService, LoHiUserInfo } from '../../auth.service';
import { CompanyService } from '../../company.service';
import { DriversService } from '../../drivers.service';
import { StatsV2Service } from '../../stats-v2.service';

@Component({
  selector: 'lohi-operations',
  templateUrl: './operations.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationsComponent implements OnInit {
  @ViewChild('driversWaiting') private driversWaitingModal: TemplateRef<any>;
  public selectedOrderCount$$ = new BehaviorSubject<number>(null);
  public selectedRegionStats$$ = new BehaviorSubject<LoHiOrderStatsRegion>(null);
  public selectedWaitingDrivers$: Observable<{ phone: string; secondsWaiting: number }[]>;
  public userInfo$: Observable<LoHiUserInfo>;
  public needsAttentionShifts$: Observable<ActiveDriver[]>;
  public regionalOrderStats$: Observable<LoHiOrderStatsRegion[]>;
  public hasUpForGrabsOrders$: Observable<boolean>;
  public memberSince$: Observable<string>;
  public exporting$$ = new BehaviorSubject<boolean>(false);
  public driversOnShiftV2$: Observable<DriverShiftOverviewResponse>;
  public driversAvailableByRegionCount$: Observable<Record<number, number>>;
  public drivers$: Observable<ActiveDriver[]>;
  public reloadStats$: Observable<DashboardData[]>;
  public hauliStats$: Observable<DashboardData[]>;
  public qualifiedDriversRegionalStatsToggle = new FormControl(true);

  constructor(
    private authService: AuthService,
    private companyService: CompanyService,
    private driversService: DriversService,
    private matDialog: MatDialog,
    private statsV2Service: StatsV2Service,
  ) {}

  public ngOnInit(): void {
    this.userInfo$ = this.authService.userInfo$;
    this.memberSince$ = this.userInfo$.pipe(
      map((userInfo) => {
        const date = new Date(userInfo.verifiedTimestamp);
        return `${date.getFullYear()}`;
      }),
    );
    this.drivers$ = this.driversService.activeDriverList$.pipe(
      map((value) => value.filter((value1) => !!value1.inProgressShift)),
    );

    // filter out regions with no drivers
    this.driversOnShiftV2$ = combineLatest([
      this.companyService.driversOnShiftV2$,
      this.driversService.activeDriverList$,
      this.driversService.availableDrivers$,
    ]).pipe(
      map(([driverShiftOverview]) => {
        const regionsWithDrivers = driverShiftOverview?.regions;
        return { regions: regionsWithDrivers } as DriverShiftOverviewResponse;
      }),
    );
    this.regionalOrderStats$ = this.companyService.regionalOrderStats$;
    this.selectedWaitingDrivers$ = combineLatest([this.selectedRegionStats$$, this.selectedOrderCount$$]).pipe(
      map(([regionalStats, orderCount]) => {
        if (!regionalStats || orderCount === null) {
          return [];
        }
        const group = regionalStats.medianSecondsToDispatchByOrderCount.find((oc) => oc.orderCount === orderCount);
        if (!group?.waitingDrivers?.length) {
          return [];
        }
        return group.waitingDrivers;
      }),
    );
    this.hasUpForGrabsOrders$ = this.regionalOrderStats$.pipe(
      map((stats) => (stats || []).some((stat) => stat.upForGrabsCount > 0)),
    );
    this.driversAvailableByRegionCount$ = this.driversService.availableDriversNow$.pipe(
      map(toRegionGroupedDriversCount),
    );
    this.reloadStats$ = this.statsV2Service.reloadStats$;
    this.hauliStats$ = this.statsV2Service.hauliStats$;
  }

  public setFilterDate(date: Date) {
    this.driversService.setDateFilter(date);
  }

  public showListOfDriversWaiting(regionalStats: LoHiOrderStatsRegion, count: number) {
    this.selectedRegionStats$$.next(regionalStats);
    this.selectedOrderCount$$.next(count);
    const temp = this.matDialog.open(this.driversWaitingModal, {
      autoFocus: false,
    });
    temp
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.selectedRegionStats$$.next(null);
        this.selectedOrderCount$$.next(null);
      });
  }

  public getBelowCommit(overviews: any) {
    return overviews.filter((value) => !!value.belowCommitment).length;
  }
}

function toRegionGroupedDriversCount(drivers: ListAvailableDriversResponseDriver[]): Record<number, number> {
  if (!drivers || !drivers.length) {
    return {};
  }

  return drivers.reduce((acc, driver) => {
    if (!acc[driver.lastRegionId]) {
      acc[driver.lastRegionId] = 0;
    }
    acc[driver.lastRegionId] += 1;
    return acc;
  }, {} as Record<number, number>);
}
